/**
 * CalendarAuthContext — Google カレンダー連携の認可状態を保持する Provider
 *
 *   linkStatus = 'unknown'  : 起動直後 / localStorage 読込前
 *   linkStatus = 'unlinked' : 未連携 (一度も問わない or 連携を明示的に解除した直後)
 *   linkStatus = 'declined' : ユーザーが「連携しない」 を選んだ (モーダルは出さない)
 *   linkStatus = 'linked'   : 連携済 (account に取得元メールアドレス)
 *
 * access_token はメモリのみ。 期限切れ時は requestAccessToken({ prompt: '' }) でサイレント再認可。
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';

const CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.events';
const STORAGE_PREFIX = 'cf-web:cal-link:';

type LinkStatus = 'unknown' | 'unlinked' | 'declined' | 'linked';

interface PersistedLinkValue {
  status: 'declined' | 'linked';
  account?: string;
}

function storageKey(loginEmail: string): string {
  return `${STORAGE_PREFIX}${loginEmail.toLowerCase()}`;
}

function readPersisted(loginEmail: string): PersistedLinkValue | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(loginEmail));
    if (!raw) return null;
    return JSON.parse(raw) as PersistedLinkValue;
  } catch {
    return null;
  }
}

function writePersisted(loginEmail: string, value: PersistedLinkValue | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.localStorage.setItem(storageKey(loginEmail), JSON.stringify(value));
    } else {
      window.localStorage.removeItem(storageKey(loginEmail));
    }
  } catch {
    /* ignore */
  }
}

interface AcquireOptions {
  /** ログイン中のメールアドレスをヒントとして渡す (= 同意画面で自動選択) */
  hint?: string;
  /** 'select_account' でアカウント選択画面を強制表示 */
  prompt?: '' | 'consent' | 'select_account';
}

interface CalendarAuthValue {
  linkStatus: LinkStatus;
  /** 連携アカウントのメールアドレス (linked のときのみ) */
  account: string | null;
  /** 現在のアクセストークン (linked + サイレント取得済のときのみ) */
  accessToken: string | null;
  /** 「連携する」 押下時に呼ぶ。 同意画面 → access_token 取得 → linked 化 */
  link: (opts?: AcquireOptions) => Promise<void>;
  /** 「連携しない」 押下時に呼ぶ */
  decline: () => void;
  /** 「カレンダー連携を解除する」 押下時に呼ぶ */
  unlink: () => void;
  /** Calendar API 呼出前に呼ぶ。 期限切れならサイレント再認可、 失敗なら null を返す */
  ensureFreshToken: () => Promise<string | null>;
}

const CalendarAuthContext = createContext<CalendarAuthValue | null>(null);

export function useCalendarAuth(): CalendarAuthValue {
  const ctx = useContext(CalendarAuthContext);
  if (!ctx) throw new Error('useCalendarAuth must be used inside <CalendarAuthProvider>');
  return ctx;
}

interface ProviderProps {
  children: ReactNode;
}

const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ?? '';

export function CalendarAuthProvider({ children }: ProviderProps): JSX.Element {
  const { user } = useAuth();
  const loginEmail = user?.email ?? '';
  const [linkStatus, setLinkStatus] = useState<LinkStatus>('unknown');
  const [account, setAccount] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const tokenExpiryRef = useRef<number>(0); // ms epoch
  const tokenClientRef = useRef<GoogleTokenClient | null>(null);
  const pendingResolveRef = useRef<((token: string | null) => void) | null>(null);

  // 起動時に localStorage を読む (ログインメールが確定してから)
  useEffect(() => {
    if (!loginEmail) {
      setLinkStatus('unknown');
      return;
    }
    const persisted = readPersisted(loginEmail);
    if (!persisted) {
      setLinkStatus('unlinked');
      setAccount(null);
    } else if (persisted.status === 'declined') {
      setLinkStatus('declined');
      setAccount(null);
    } else {
      setLinkStatus('linked');
      setAccount(persisted.account ?? null);
    }
  }, [loginEmail]);

  const ensureTokenClient = useCallback((): GoogleTokenClient | null => {
    if (tokenClientRef.current) return tokenClientRef.current;
    if (!GOOGLE_CLIENT_ID) return null;
    if (!window.google?.accounts?.oauth2) return null;
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: CALENDAR_SCOPE,
      callback: (resp) => {
        const resolver = pendingResolveRef.current;
        pendingResolveRef.current = null;
        if (resp.error || !resp.access_token) {
          if (resolver) resolver(null);
          return;
        }
        const expiresAtMs = Date.now() + (resp.expires_in - 30) * 1000;
        setAccessToken(resp.access_token);
        tokenExpiryRef.current = expiresAtMs;
        if (resolver) resolver(resp.access_token);
      },
      error_callback: () => {
        const resolver = pendingResolveRef.current;
        pendingResolveRef.current = null;
        if (resolver) resolver(null);
      },
    });
    tokenClientRef.current = client;
    return client;
  }, []);

  const requestToken = useCallback(
    (override?: AcquireOptions): Promise<string | null> => {
      const client = ensureTokenClient();
      if (!client) return Promise.resolve(null);
      return new Promise<string | null>((resolve) => {
        pendingResolveRef.current = resolve;
        client.requestAccessToken({ prompt: override?.prompt ?? '' });
      });
    },
    [ensureTokenClient]
  );

  const link = useCallback(
    async (opts?: AcquireOptions) => {
      const token = await requestToken(opts);
      if (!token || !loginEmail) return;
      // GIS から userinfo は別途引かないとアカウントメールが取れないため、 一旦 hint を当てるか
      // ログインメールを保持先として使う (実用上は ID トークンの sub と一致するはず)
      const chosenAccount = opts?.hint ?? loginEmail;
      setLinkStatus('linked');
      setAccount(chosenAccount);
      writePersisted(loginEmail, { status: 'linked', account: chosenAccount });
    },
    [requestToken, loginEmail]
  );

  const decline = useCallback(() => {
    if (!loginEmail) return;
    setLinkStatus('declined');
    setAccount(null);
    setAccessToken(null);
    writePersisted(loginEmail, { status: 'declined' });
  }, [loginEmail]);

  const unlink = useCallback(() => {
    if (!loginEmail) return;
    setLinkStatus('unlinked');
    setAccount(null);
    setAccessToken(null);
    tokenExpiryRef.current = 0;
    writePersisted(loginEmail, null);
  }, [loginEmail]);

  const ensureFreshToken = useCallback(async (): Promise<string | null> => {
    if (linkStatus !== 'linked') return null;
    if (accessToken && Date.now() < tokenExpiryRef.current) return accessToken;
    return requestToken({ prompt: '' });
  }, [linkStatus, accessToken, requestToken]);

  const value = useMemo<CalendarAuthValue>(
    () => ({ linkStatus, account, accessToken, link, decline, unlink, ensureFreshToken }),
    [linkStatus, account, accessToken, link, decline, unlink, ensureFreshToken]
  );

  return <CalendarAuthContext.Provider value={value}>{children}</CalendarAuthContext.Provider>;
}
